// Keyed, debounced, serialized autosave queue with retry. One key per step or tool row:
// the latest job for a key replaces any queued one, and a key never runs twice at once.
import { ApiError } from '../lib/api';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type Job = () => Promise<void>;

export class Saver {
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private queued = new Map<string, Job>();
  private running = new Map<string, Promise<void>>();
  private failures = new Map<string, number>();
  private listeners = new Set<(s: SaveStatus) => void>();
  status: SaveStatus = 'idle';

  constructor(private onFatal: (err: ApiError) => void = () => {}) {}

  subscribe(fn: (s: SaveStatus) => void) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private set(s: SaveStatus) { this.status = s; this.listeners.forEach((l) => l(s)); }

  get pending(): boolean { return this.timers.size > 0 || this.running.size > 0 || this.queued.size > 0; }

  schedule(key: string, job: Job, delayMs: number) {
    this.queued.set(key, job);
    clearTimeout(this.timers.get(key));
    this.timers.set(key, setTimeout(() => { this.timers.delete(key); void this.run(key); }, delayMs));
  }

  private async run(key: string): Promise<void> {
    const inFlight = this.running.get(key);
    if (inFlight) { await inFlight; if (!this.timers.has(key) && this.queued.has(key)) return this.run(key); return; }
    const job = this.queued.get(key);
    if (!job) return;
    this.queued.delete(key);
    this.set('saving');
    const p = (async () => {
      try {
        await job();
        this.failures.delete(key);
        if (!this.pending || [...this.running.keys()].every((k) => k === key)) {
          this.set(this.failures.size ? 'error' : 'saved');
        }
      } catch (e) {
        const err = e instanceof ApiError ? e : new ApiError(0, 'network', String(e));
        if (err.retryable) {
          const n = (this.failures.get(key) ?? 0) + 1;
          this.failures.set(key, n);
          this.set('error');
          // Retry unless a newer edit already queued its own job.
          if (!this.queued.has(key)) this.schedule(key, job, Math.min(30_000, 1000 * 2 ** n));
        } else {
          this.failures.delete(key);
          this.set('error');
          this.onFatal(err);
        }
      }
    })();
    this.running.set(key, p);
    try { await p; } finally { this.running.delete(key); }
    if (this.queued.has(key) && !this.timers.has(key)) await this.run(key);
  }

  /** Runs everything queued now and waits. Resolves true when nothing failed. */
  async flush(): Promise<boolean> {
    const keys = [...new Set([...this.timers.keys(), ...this.queued.keys()])];
    keys.forEach((k) => { clearTimeout(this.timers.get(k)); this.timers.delete(k); });
    await Promise.all([...keys.map((k) => this.run(k)), ...this.running.values()]);
    return this.failures.size === 0 && this.status !== 'error';
  }

  dispose() { this.timers.forEach((t) => clearTimeout(t)); this.timers.clear(); this.listeners.clear(); }
}

export function savedLabel(status: SaveStatus, recentlySaved: boolean): string {
  if (status === 'saving') return 'Saving…';
  if (status === 'error') return 'Couldn’t save — retrying';
  if (status === 'saved' && recentlySaved) return 'Saved just now';
  return 'All changes saved';
}
